import { ref } from 'vue'

/** LocalStorage keys used for persisting visibility */
export const PRODUCTIVITY_VISIBLE_KEY = 'rws.productivity.visible'
export const PRODUCTIVITY_HIDDEN_ALT_KEY = 'rws_productivity_visible'

/** Read initial visibility state from localStorage */
function checkProductivityVisibility(): boolean {
    if (typeof localStorage === 'undefined') return true
    const val1 = localStorage.getItem(PRODUCTIVITY_VISIBLE_KEY)
    const val2 = localStorage.getItem(PRODUCTIVITY_HIDDEN_ALT_KEY)
    if (val1 === 'false' || val2 === 'false') return false
    return true
}

/** Reactive ref tracking productivity module visibility */
export const isProductivityVisible = ref<boolean>(checkProductivityVisibility())

/** Updates productivity visibility in state and localStorage */
export function setProductivityVisibility(visible: boolean): void {
    isProductivityVisible.value = visible
    if (typeof localStorage !== 'undefined') {
        if (visible) {
            localStorage.removeItem(PRODUCTIVITY_VISIBLE_KEY)
            localStorage.removeItem(PRODUCTIVITY_HIDDEN_ALT_KEY)
            console.log(
                '%c[RWS Console] Productivity module is now VISIBLE.',
                'color: #10b981; font-weight: bold;'
            )
        } else {
            localStorage.setItem(PRODUCTIVITY_VISIBLE_KEY, 'false')
            localStorage.setItem(PRODUCTIVITY_HIDDEN_ALT_KEY, 'false')
            console.log(
                '%c[RWS Console] Productivity module is now HIDDEN (persisted in localStorage).',
                'color: #f59e0b; font-weight: bold;'
            )
        }
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(
            new CustomEvent('rws-productivity-visibility-changed', { detail: { visible } })
        )
    }
}

/** Initialize global `rws` console utilities on window */
export function initConsoleCommands(): void {
    if (typeof window === 'undefined') return

    // Sync state if storage changes externally (e.g. edited in DevTools Application tab)
    window.addEventListener('storage', (e) => {
        if (e.key === PRODUCTIVITY_VISIBLE_KEY || e.key === PRODUCTIVITY_HIDDEN_ALT_KEY) {
            isProductivityVisible.value = checkProductivityVisibility()
        }
    })

    const productivityController: Record<string, any> = {}

    // Property getter & setter for `rws.productivity.visible`
    Object.defineProperty(productivityController, 'visible', {
        get() {
            return isProductivityVisible.value
        },
        set(val: boolean) {
            setProductivityVisibility(Boolean(val))
        },
        configurable: true,
        enumerable: true,
    })

    // Methods helper: `rws.productivity.hide()`, `rws.productivity.show()`, `rws.productivity.status()`
    Object.assign(productivityController, {
        hide() {
            setProductivityVisibility(false)
            return 'Productivity module hidden.'
        },
        show() {
            setProductivityVisibility(true)
            return 'Productivity module visible.'
        },
        status() {
            return isProductivityVisible.value ? 'Visible' : 'Hidden'
        },
    })

    const rwsObj = (window as any).rws || {}
    rwsObj.productivity = productivityController

    // Sentry developer utilities
    rwsObj.sentry = {
        test(message = 'Test error triggered via rws.sentry.test()') {
            const err = new Error(message)
            console.log('%c[RWS Console] Triggering test Sentry exception…', 'color: #ef4444; font-weight: bold;')
            import('@/core/sentry').then(({ captureException, isSentryInitialized }) => {
                if (!isSentryInitialized()) {
                    console.warn('[RWS Console] Sentry is not initialized (VITE_SENTRY_DSN not configured). Exception was logged locally.')
                } else {
                    captureException(err, { source: 'console_command', test: true })
                    console.log('%c[RWS Console] Sentry event dispatched successfully.', 'color: #10b981; font-weight: bold;')
                }
            })
            return 'Dispatched test error to Sentry.'
        },
        status() {
            import('@/core/sentry').then(({ isSentryInitialized }) => {
                import('@/config').then(({ appConfig }) => {
                    console.log('[RWS Console] Sentry Status:', {
                        initialized: isSentryInitialized(),
                        environment: appConfig.sentryEnvironment,
                        dsnConfigured: Boolean(appConfig.sentryDsn),
                    })
                })
            })
            return 'Checking Sentry status…'
        },
    }

    ;(window as any).rws = rwsObj
}
